import * as React from 'react';
import { Box, Chip, styled, Typography } from '@mui/material';
import Done from '@mui/icons-material/Done';
import Close from '@mui/icons-material/Close';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { Option } from 'lib/types';
import { FormikValues } from 'formik';
import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { SettingDisplay } from 'lib/SettingDisplay';

const timeslotsProviderOptions: Option[] = [
    { label: 'Pepper', value: 'PEPPER' },
    { label: 'Zonal', value: 'ZONAL' }
];

const PREFIX = 'TimeslotsSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const TimeslotsSettings = ({ settings, onSubmit }: IProps) => {
    const timeslotsProviderLabel = React.useMemo(
        () =>
            settings
                ? timeslotsProviderOptions.find(option => option.value === settings.timeSlotsProvider)
                      ?.label ?? 'None'
                : 'None',
        [settings]
    );

    const renderTimeslotsEdit = React.useCallback(
        (values: FormikValues) => (
            <Box display="flex" flexDirection="column">
                <SettingDisplay label="Timeslots Enabled">
                    <SwitchFormField name="timeslotsEnabled" />
                </SettingDisplay>
                <SettingDisplay label="Timeslots Provider">
                    <SelectFormField name="timeslotsProvider" options={timeslotsProviderOptions} />
                </SettingDisplay>
                {values?.timeslotsProvider === 'PEPPER' && (
                    <>
                        <SettingDisplay
                            label="Standard Orders Per Timeslot"
                            description="The number of orders that can be made within a selected timeslot"
                        >
                            <NumberFormField
                                shouldSeparateThousands={false}
                                name="standardOrdersPerTimeslot"
                            />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Standard Timeslot Frequency Mins"
                            description="How many minutes after a new timeslot can start"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="standardFrequencyInMins" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Standard Timeslot Length Mins"
                            description="The duration of each timeslot"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="standardLengthInMins" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Standard Window Length Mins"
                            description="How far ahead a user can make an order for"
                        >
                            <NumberFormField
                                shouldSeparateThousands={false}
                                name="standardWindowLengthInMins"
                            />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Standard Window Offset Mins"
                            description="An offset from the current time of when a user is able to request an order"
                        >
                            <NumberFormField
                                shouldSeparateThousands={false}
                                name="standardWindowOffsetInMins"
                            />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Busy Orders Per Timeslot"
                            description="The number of orders that can be made within a selected timeslot"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="busyOrdersPerTimeslot" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Busy Timeslot Frequency Mins"
                            description="How many minutes after a new timeslot can start"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="busyFrequencyInMins" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Busy Timeslot Length Mins"
                            description="The duration of each timeslot"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="busyLengthInMins" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Busy Window Length Mins"
                            description="How far ahead a user can make an order for"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="busyWindowLengthInMins" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Busy Window Offset Mins"
                            description="An offset from the current time of when a user is able to request an order"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="busyWindowOffsetInMins" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Quiet Orders Per Timeslot"
                            description="The number of orders that can be made within a selected timeslot"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="quietOrdersPerTimeslot" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Quiet Timeslot Frequency Mins"
                            description="How many minutes after a new timeslot can start"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="quietFrequencyInMins" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Quiet Timeslot Length Mins"
                            description="The duration of each timeslot"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="quietLengthInMins" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Quiet Window Length Mins"
                            description="How far ahead a user can make an order for"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="quietWindowLengthInMins" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Quiet Window Offset Mins"
                            description="An offset from the current time of when a user is able to request an order"
                        >
                            <NumberFormField shouldSeparateThousands={false} name="quietWindowOffsetInMins" />
                        </SettingDisplay>
                    </>
                )}
            </Box>
        ),
        []
    );

    return (
        <StyledMuiCardForm
            title="Timeslots"
            initialValues={{
                timeslotsEnabled: settings?.timeSlotsEnabled ?? false,
                timeslotsProvider: settings?.timeSlotsProvider,
                standardOrdersPerTimeslot: settings?.timeSlots?.standard?.ordersPerTimeslot,
                standardFrequencyInMins: settings?.timeSlots?.standard?.timeslotFrequencyInMins,
                standardLengthInMins: settings?.timeSlots?.standard?.timeslotLengthInMins,
                standardWindowLengthInMins: settings?.timeSlots?.standard?.windowLengthInMins,
                standardWindowOffsetInMins: settings?.timeSlots?.standard?.windowOffsetInMins,
                busyOrdersPerTimeslot: settings?.timeSlots?.busy?.ordersPerTimeslot,
                busyFrequencyInMins: settings?.timeSlots?.busy?.timeslotFrequencyInMins,
                busyLengthInMins: settings?.timeSlots?.busy?.timeslotLengthInMins,
                busyWindowLengthInMins: settings?.timeSlots?.busy?.windowLengthInMins,
                busyWindowOffsetInMins: settings?.timeSlots?.busy?.windowOffsetInMins,
                quietOrdersPerTimeslot: settings?.timeSlots?.quiet?.ordersPerTimeslot,
                quietFrequencyInMins: settings?.timeSlots?.quiet?.timeslotFrequencyInMins,
                quietLengthInMins: settings?.timeSlots?.quiet?.timeslotLengthInMins,
                quietWindowLengthInMins: settings?.timeSlots?.quiet?.windowLengthInMins,
                quietWindowOffsetInMins: settings?.timeSlots?.quiet?.windowOffsetInMins
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Timeslots Enabled">
                        {settings?.timeSlotsEnabled && <Done />}
                        {!settings?.timeSlotsEnabled && <Close />}
                    </SettingDisplay>
                    <SettingDisplay label="Timeslots Provider">
                        <Chip color="primary" label={timeslotsProviderLabel} />
                    </SettingDisplay>
                    {settings?.timeSlotsProvider === 'PEPPER' && (
                        <>
                            <SettingDisplay label="Standard Orders Per Timeslot">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.standard?.ordersPerTimeslot}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Standard Timeslot Frequency Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.standard?.timeslotFrequencyInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Standard Timeslot Length Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.standard?.timeslotLengthInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Standard Window Length Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.standard?.windowLengthInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Standard Window Offset Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.standard?.windowOffsetInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Busy Orders Per Timeslot">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.busy?.ordersPerTimeslot}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Busy Timeslot Frequency Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.busy?.timeslotFrequencyInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Busy Timeslot Length Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.busy?.timeslotLengthInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Busy Window Length Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.busy?.windowLengthInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Busy Window Offset Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.busy?.windowOffsetInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Quiet Orders Per Timeslot">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.quiet?.ordersPerTimeslot}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Quiet Timeslot Frequency Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.quiet?.timeslotFrequencyInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Quiet Timeslot Length Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.quiet?.timeslotLengthInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Quiet Window Length Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.quiet?.windowLengthInMins}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Quiet Window Offset Mins">
                                <Typography variant="body2">
                                    {settings?.timeSlots?.quiet?.windowOffsetInMins}
                                </Typography>
                            </SettingDisplay>
                        </>
                    )}
                </Box>
            }
            childrenWhileEditing={renderTimeslotsEdit}
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
